import { RefObject, useState } from 'react';
import { toast } from 'react-toastify';
import { downloadTablesAsExcel, ExportSheet } from '../helpers/TableExcelExport';

interface ExcelExportButtonProps {
	/** The element holding the report's tables. */
	targetRef: RefObject<HTMLElement>;
	title: string;
	/** File name without .xlsx. */
	filename: string;
	/** Filter lines printed under the title. */
	meta?: string[];
	disabled?: boolean;
	className?: string;
	/** Sheets made from data, put before the tables; called when the button is pressed. */
	extraSheets?: () => ExportSheet[];
}

/** "Excel" button: downloads the report's tables, as shown, as an .xlsx file. */
export default function ExcelExportButton({ targetRef, title, filename, meta, disabled, className, extraSheets }: ExcelExportButtonProps) {
	const [exporting, setExporting] = useState(false);

	const handleExport = async () => {
		if (!targetRef.current) {
			return;
		}
		setExporting(true);
		try {
			await downloadTablesAsExcel(targetRef.current, { title, filename, meta, extraSheets: extraSheets ? extraSheets() : [] });
		} catch (error: any) {
			toast.error(error?.message || 'The Excel file could not be made.', { position: 'bottom-right', autoClose: 4000 });
		} finally {
			setExporting(false);
		}
	};

	return (
		<button
			type='button'
			className={className || 'btn btn-sm btn-success p-3'}
			onClick={handleExport}
			disabled={disabled || exporting}
			title='Download the report as an Excel file'
		>
			{exporting
				? <span className='spinner-border spinner-border-sm me-1'></span>
				: <i className='fas fa-file-excel me-1'></i>}
			Excel
		</button>
	);
}
